import React, { useState, useEffect, useRef } from 'react';
import comms from '../../comms';

const Messages = ({ 
  isOpen, 
  onClose, 
  vehicleNumber, 
  driverName = 'Driver',
  messages = [],
  onSendMessage,
  onMarkAsRead
}) => {
  const [messageInput, setMessageInput] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedHistory, setHasLoadedHistory] = useState(false);
  const messagesEndRef = useRef(null);

  // Scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory, messages]);

  // Load chat history when component opens for the first time
  useEffect(() => {
    if (isOpen && !hasLoadedHistory) {
      loadChatHistory();
    }
  }, [isOpen, hasLoadedHistory]);

  // Set up real-time message listening
  useEffect(() => {
    if (isOpen && comms.getSocket()) {
      const socket = comms.getSocket();
      
      // Ensure we're joined to the driver app room
      comms.joinDriverApp();
      
      const handleNewMessage = (messageData) => {
        console.log('Received chat:newMessage:', messageData);
        // Only process messages for this vehicle and only MSG/MSGM types
        if ((messageData.vehicleCode === vehicleNumber || messageData.unitId === vehicleNumber) &&
            (messageData.type === 'MSG' || messageData.type === 'MSGM')) {
          const newMessage = {
            id: `${messageData.timestamp}-${messageData.from}`,
            text: messageData.message,
            timestamp: messageData.timestamp,
            // Fix: Messages from dispatch to driver should be 'received', messages from driver should be 'sent'
            type: messageData.direction === 'Out' ? 'received' : 'sent',
            // Fix: For incoming messages to driver, sender should be 'Dispatch' when direction is 'Out'
            from: messageData.direction === 'Out' ? 'Dispatch' : (messageData.from || driverName)
          };
          
          setChatHistory(prevHistory => {
            // Avoid duplicates
            const exists = prevHistory.some(msg => 
              msg.id === newMessage.id || 
              (msg.text === newMessage.text && 
               Math.abs(new Date(msg.timestamp) - new Date(newMessage.timestamp)) < 1000)
            );
            
            if (!exists) {
              return [...prevHistory, newMessage].sort((a, b) => 
                new Date(a.timestamp) - new Date(b.timestamp)
              );
            }
            return prevHistory;
          });
        }
      };

      // Handle delta:message events (single new message)
      const handleDeltaMessage = (messageData) => {
        console.log('Received delta:message:', messageData);
        // Only process messages for this vehicle and only MSG type
        if ((messageData.vehicleCode === vehicleNumber || messageData.unitId === vehicleNumber) &&
            messageData.type === 'MSG') {
          const newMessage = {
            id: messageData._id || `${messageData.timestamp}-${messageData.from}`,
            text: messageData.data || messageData.message,
            timestamp: messageData.timestamp,
            // Fix: Messages from dispatch to driver should be 'received', messages from driver should be 'sent'
            type: messageData.direction === 'Out' ? 'received' : 'sent',
            // Fix: For incoming messages to driver, sender should be 'Dispatch' when direction is 'Out'
            from: messageData.direction === 'Out' ? 'Dispatch' : (messageData.from || driverName)
          };
          
          setChatHistory(prevHistory => {
            // Avoid duplicates
            const exists = prevHistory.some(msg => 
              msg.id === newMessage.id || 
              (msg.text === newMessage.text && 
               Math.abs(new Date(msg.timestamp) - new Date(newMessage.timestamp)) < 1000)
            );
            
            if (!exists) {
              return [...prevHistory, newMessage].sort((a, b) => 
                new Date(a.timestamp) - new Date(b.timestamp)
              );
            }
            return prevHistory;
          });
        }
      };

      // Handle delta:messages events (all current messages)
      const handleDeltaMessages = (data) => {
        console.log('Received delta:messages:', data);
        if (Array.isArray(data)) {
          // Handle array of messages
          const formattedMessages = data
            .filter(msg => msg.type === 'MSG' && 
              (msg.vehicleCode === vehicleNumber || msg.unitId === vehicleNumber))
            .map(msg => ({
              id: msg._id || `${msg.timestamp}-${msg.from}`,
              text: msg.data || msg.message,
              timestamp: msg.timestamp,
              // Fix: Messages from dispatch to driver should be 'received', messages from driver should be 'sent'
              type: msg.direction === 'Out' ? 'received' : 'sent',
              // Fix: For incoming messages to driver, sender should be 'Dispatch' when direction is 'Out'
              from: msg.direction === 'Out' ? 'Dispatch' : (msg.from || driverName)
            }));
          
          setChatHistory(formattedMessages.sort((a, b) => 
            new Date(a.timestamp) - new Date(b.timestamp)
          ));
        } else {
          // Handle single message in delta:messages format
          handleDeltaMessage(data);
        }
      };

      // Handle traditional Command messages (MSG type)
      const handleCommand = (command) => {
        console.log('Received command:', command);
        const parts = command.split('|');
        const commandType = parts[1]; // Skip the message ID part
        
        if (commandType === 'MSG' && parts[2]) {
          const newMessage = {
            id: `${Date.now()}-dispatch`,
            text: parts[2],
            timestamp: new Date().toISOString(),
            type: 'received',
            from: 'Dispatch'
          };
          
          setChatHistory(prevHistory => {
            // Avoid duplicates
            const exists = prevHistory.some(msg => 
              msg.text === newMessage.text && 
              Math.abs(new Date(msg.timestamp) - new Date(newMessage.timestamp)) < 2000
            );
            
            if (!exists) {
              return [...prevHistory, newMessage].sort((a, b) => 
                new Date(a.timestamp) - new Date(b.timestamp)
              );
            }
            return prevHistory;
          });
        }
      };

      socket.on('chat:newMessage', handleNewMessage);
      socket.on('delta:message', handleDeltaMessage);
      socket.on('delta:messages', handleDeltaMessages);
      socket.on('Command', handleCommand);

      return () => {
        socket.off('chat:newMessage', handleNewMessage);
        socket.off('delta:message', handleDeltaMessage);
        socket.off('delta:messages', handleDeltaMessages);
        socket.off('Command', handleCommand);
      };
    }
  }, [isOpen, vehicleNumber, driverName]);

  // Combine and sort all messages when they change
  useEffect(() => {
    // Always merge external messages with chat history, regardless of hasLoadedHistory
    if (messages && Array.isArray(messages) && messages.length > 0) {
      // Filter incoming messages to only include MSG and MSGM types, then combine with chat history
      const filteredMessages = messages.filter(msg => 
        (msg.type === 'received' || msg.type === 'sent') // These are the message types from Dispatch component
      ).map(msg => ({
        id: msg.id || `${msg.timestamp}-${msg.from}`,
        text: msg.text,
        timestamp: msg.timestamp.toISOString ? msg.timestamp.toISOString() : msg.timestamp,
        type: msg.type,
        from: msg.from
      }));
      
      // Combine external messages with chat history
      setChatHistory(prevHistory => {
        const allMessages = [...prevHistory, ...filteredMessages];
        
        // Remove duplicates based on message content and timestamp
        const uniqueMessages = allMessages.filter((message, index, arr) => {
          return index === arr.findIndex(m => 
            m.text === message.text && 
            Math.abs(new Date(m.timestamp) - new Date(message.timestamp)) < 2000
          );
        });
        
        return uniqueMessages.sort((a, b) => 
          new Date(a.timestamp) - new Date(b.timestamp)
        );
      });
    }
  }, [messages]); // Remove hasLoadedHistory dependency

  // Mark messages as read when modal opens
  useEffect(() => {
    if (isOpen && onMarkAsRead) {
      onMarkAsRead();
    }
  }, [isOpen, onMarkAsRead]);

  const loadChatHistory = async () => {
    if (!vehicleNumber) return;
    
    console.log('Loading chat history for vehicle:', vehicleNumber);
    setIsLoading(true);
    try {
      // Request chat history from server using the driverApp message system
      const socket = comms.getSocket();
      if (socket) {
        console.log('Emitting driverAppMessage with getChatHistory command');
        socket.emit('driverAppMessage', {
          command: 'getChatHistory',
          vehicleNumber: vehicleNumber
        });

        // Listen for the response
        const handleChatHistory = (history) => {
          console.log('Received chatHistory response:', history);
          if (history && !history.error && Array.isArray(history)) {
            // Format server messages to match our format and filter for MSG/MSGM types only
            const formattedHistory = history
              .filter(msg => msg.type === 'MSG' || msg.type === 'MSGM') // Only show MSG and MSGM messages
              .map(msg => ({
                id: msg._id || `${msg.timestamp}-${msg.unitId}`,
                text: msg.data || msg.message,
                timestamp: msg.timestamp || msg.createdAt,
                // Fix: Messages with direction 'Out' are FROM dispatch TO driver (should be 'received')
                // Messages with direction 'In' are FROM driver TO dispatch (should be 'sent')
                type: msg.direction === 'Out' ? 'received' : 'sent',
                // Fix: For direction 'Out' (dispatch to driver), sender should be 'Dispatch'
                // For direction 'In' (driver to dispatch), sender should be the driver name
                from: msg.direction === 'Out' ? 'Dispatch' : driverName
              }));
            console.log('Formatted chat history:', formattedHistory);
            setChatHistory(formattedHistory);
          } else if (history && history.error) {
            console.error('Error in chat history response:', history.error);
          }
          setHasLoadedHistory(true);
          setIsLoading(false);
        };

        socket.on('chatHistory', handleChatHistory);

        // Clean up listener after receiving response
        setTimeout(() => {
          socket.off('chatHistory', handleChatHistory);
        }, 5000);
      } else {
        console.error('Socket not available for chat history request');
        setIsLoading(false);
        setHasLoadedHistory(true);
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
      setIsLoading(false);
      setHasLoadedHistory(true);
    }
  };

  const handleSendMessage = () => {
    if (messageInput.trim()) {
      const socket = comms.getSocket();
      
      if (socket && vehicleNumber) {
        console.log('Sending message from vehicle:', vehicleNumber, 'message:', messageInput.trim());
        
        // Create the message object for driver messages
        const messageData = {
          vehicleId: vehicleNumber,
          vehicleCode: vehicleNumber,
          unitId: vehicleNumber,
          message: messageInput.trim(),
          from: driverName
        };

        // Immediately add the message to the chat history for instant feedback
        const newMessage = {
          id: `${Date.now()}-${driverName}`,
          text: messageInput.trim(),
          timestamp: new Date().toISOString(),
          type: 'sent',
          from: driverName
        };

        setChatHistory(prevHistory => {
          const updatedHistory = [...prevHistory, newMessage].sort((a, b) => 
            new Date(a.timestamp) - new Date(b.timestamp)
          );
          return updatedHistory;
        });

        console.log('Emitting driver:sendMessage with data:', messageData);
        
        // Use the correct event for driver messages
        socket.emit('driver:sendMessage', messageData, (response) => {
          console.log('Response from driver:sendMessage:', response);
          if (response && response.success) {
            console.log('Message sent successfully');
          } else {
            console.error('Failed to send message:', response?.error);
            // Remove the message from history if sending failed
            setChatHistory(prev => prev.filter(msg => msg.id !== newMessage.id));
          }
        });

        // Clear input
        setMessageInput('');

        // Optionally call the onSendMessage callback if provided
        if (onSendMessage) {
          onSendMessage(messageInput.trim());
        }
      } else {
        console.error('Socket or vehicleNumber not available for sending message');
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  const groupMessagesByDate = (messages) => {
    const groups = {};
    messages.forEach(message => {
      const date = formatDate(message.timestamp || message.CreatedOn || message.time);
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(message);
    });
    return groups;
  };

  if (!isOpen) return null;

  const messageGroups = groupMessagesByDate(chatHistory);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-md mx-4 flex flex-col" style={{ height: '80vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-bold">Messages - Vehicle {vehicleNumber}</h3>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {isLoading ? (
            <div className="text-center text-gray-500">Loading chat history...</div>
          ) : (
            Object.entries(messageGroups).map(([date, messages]) => (
              <div key={date}>
                {/* Date Separator */}
                <div className="text-center text-xs text-gray-500 mb-3">
                  <div className="bg-gray-100 inline-block px-2 py-1 rounded">
                    {date}
                  </div>
                </div>

                {/* Messages for this date */}
                {messages.map((message, index) => (
                  <div
                    key={message.id || index}
                    className={`flex mb-3 ${
                      message.type === 'sent' ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    <div
                      className={`max-w-xs px-3 py-2 rounded-lg ${
                        message.type === 'sent'
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-200 text-gray-800'
                      }`}
                    >
                      <div className="text-sm">{message.text}</div>
                      <div
                        className={`text-xs mt-1 ${
                          message.type === 'sent' ? 'text-blue-100' : 'text-gray-500'
                        }`}
                      >
                        {formatTime(message.timestamp || message.CreatedOn || message.time)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="border-t border-gray-200 p-4">
          <div className="flex space-x-2">
            <textarea
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:border-blue-500"
              rows="1"
              style={{ minHeight: '40px', maxHeight: '100px' }}
            />
            <button
              onClick={handleSendMessage}
              disabled={!messageInput.trim()}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Messages;